import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { updatedDiff } from "deep-object-diff";
import isEmpty from "lodash/isEmpty";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Tooltip, Switch } from "../../../utils/local-antd";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { PermissionAction } from "../../../model/permissions/types/PermissionAction";
import { PermissionType } from "../../../model/permissions/types/PermissionType";
import { User } from "../../../model/user/User";
import { permissionActions } from "../../../redux/permission/permission.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { sortPermissions } from "../utils/permissionUtils";

const Title = styled(Text)<{ horizontal?: boolean }>`
  font-weight: bold;
  flex: 1;
  ${({ horizontal }) =>
    horizontal
      ? `
    padding-inline-end: ${Spacing.s}
  `
      : ""}
`;

const TableHeader = styled(Flex)`
  justify-content: space-between;
  padding: ${Spacing.s};
  border-bottom: ${({ theme }) => `2px solid ${theme.darkerSub}`};
`;

const TableRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.m} ${Spacing.s};
  border-bottom: ${({ theme }) => `1px solid ${theme.darkSub}`};
`;

const PERMISSION_ORDER = [
  PermissionType.ENTRY,
  PermissionType.FORM,
  PermissionType.KANBAN,
  PermissionType.KANBAN_CARD,
  PermissionType.LEAD_TASK,
  PermissionType.CONTACT_TASK,
  PermissionType.KANBAN_TASK,
  PermissionType.ORPHAN_TASK,
];

interface MemberPermissionsProps {
  member: User;
}

export const MemberPermissions: React.FC<MemberPermissionsProps> = ({
  member,
}) => {
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState<
    Record<PermissionType, Record<PermissionAction, boolean>>
  >(
    (member.permissions?.permissions as Record<
      PermissionType,
      Record<PermissionAction, boolean>
    >) || ({} as Record<PermissionType, Record<PermissionAction, boolean>>),
  );

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const handleSwitch = (
    value: boolean,
    type: PermissionType,
    action: PermissionAction,
  ) => {
    setPermissions((prev) => {
      const currentPermissions = { ...prev[type] };

      if (action === PermissionAction.READ && !value) {
        Object.keys(currentPermissions).forEach((key) => {
          if (key !== PermissionAction.READ) {
            currentPermissions[key as PermissionAction] = false;
          }
        });
      }

      currentPermissions[action] = value;

      return {
        ...prev,
        [type]: currentPermissions,
      };
    });
  };

  const onUpdateUserPermission = async () => {
    try {
      setLoading(true);
      await dispatch(
        permissionActions.updateMemberPermissions({
          userId,
          memberId: member._id,
          permissions,
        }),
      ).unwrap();
      await dispatch(permissionActions.getMembers({ userId })).unwrap();
      AtiraToast.success(t("permissions.memberPermissions.updateSuccess"));
    } catch (e: any) {
      console.error(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const ordered: Record<string, any> = {};
    const perms = member.permissions?.permissions;
    PERMISSION_ORDER.forEach((key) => {
      if (perms[key as PermissionType]) {
        ordered[key] = perms[key as PermissionType];
      }
    });
    Object.keys(perms).forEach((key) => {
      if (!ordered[key]) {
        ordered[key] = perms[key as PermissionType];
      }
    });
    setPermissions(ordered);
  }, [member.permissions?.permissions]);

  return (
    <Flex flexDirection="column" width="100%">
      <Flex justifyContent="flex-end" padding="s">
        <Button
          hover
          margin="none"
          title={t("common.save")}
          disabled={isEmpty(
            updatedDiff(permissions, member.permissions?.permissions || {}),
          )}
          loading={loading}
          onClick={onUpdateUserPermission}
        />
      </Flex>

      <TableHeader>
        <Flex flex={1} />

        <Flex flex={1}>
          <Tooltip
            title={t("permissions.memberPermissions.read.notice.disabled")}
          >
            <Flex alignItems="center" gap="s">
              <AtiraIcon icon={faInfoCircle} color="main" />
              <Title>{t("common.read")}</Title>
            </Flex>
          </Tooltip>
        </Flex>

        <Title>{t("common.create")}</Title>
        <Title>{t("common.update")}</Title>
        <Title>{t("common.delete")}</Title>
      </TableHeader>

      {Object.entries(permissions || {}).map(([type, value]) => (
        <TableRow key={type}>
          <Title horizontal>{t(`common.${type.toLowerCase()}`)}</Title>

          {Object.entries(sortPermissions(value)).map(([action, checked]) => {
            const isReadDisabled = !value.READ;
            const tooltipTitle =
              action !== PermissionAction.READ && isReadDisabled
                ? t("permissions.memberPermissions.switch.disabled.note")
                : "";

            return (
              <Flex flex={1}>
                <Tooltip title={tooltipTitle}>
                  <Switch
                    key={action}
                    disabled={
                      loading ||
                      (action !== PermissionAction.READ && !value.READ)
                    }
                    checked={checked}
                    onChange={(isChecked) =>
                      handleSwitch(
                        isChecked,
                        type as PermissionType,
                        action as PermissionAction,
                      )
                    }
                  />
                </Tooltip>
              </Flex>
            );
          })}
        </TableRow>
      ))}
    </Flex>
  );
};
